/**
 * One checkout at a time per page. Every BuyEventButton reads this store with
 * `useSyncExternalStore`, so while one plan's checkout is starting, all of
 * them are disabled and a second tap is ignored before any analytics or
 * server call. Two taps used to start two Stripe sessions, and whichever
 * redirect won opened, often the plan the visitor didn't choose.
 *
 * The lock is released when the checkout fails or throws, when the page comes
 * back from the bfcache (← on Stripe), and after a safety timeout.
 */

export const CHECKOUT_LOCK_TIMEOUT_MS = 20_000;

/** "redirecting" keeps the lock while the browser leaves for Stripe. */
export type CheckoutRunResult = "redirecting" | "released";

let owner: string | null = null;
// Each run gets its own number, so a stale run (after the safety timeout or a
// bfcache restore) can't release a newer one, even from the same button.
let ownerRun = 0;
let lastRun = 0;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();
let isPageShowListening = false;

function setOwner(next: string | null, run = 0): void {
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
  owner = next;
  ownerRun = run;
  if (next !== null) {
    safetyTimer = setTimeout(() => releaseRun(run), CHECKOUT_LOCK_TIMEOUT_MS);
  }
  listeners.forEach((listener) => listener());
}

function releaseRun(run: number): void {
  if (owner !== null && ownerRun === run) setOwner(null);
}

/** The id of the button whose checkout is starting, or null. */
export function getCheckoutOwner(): string | null {
  return owner;
}

export function subscribeCheckout(listener: () => void): () => void {
  if (!isPageShowListening && typeof window !== "undefined") {
    isPageShowListening = true;
    window.addEventListener("pageshow", releaseOnPageShow);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A page restored from the bfcache (back from Stripe) gets live buttons. */
export function releaseOnPageShow(event: { persisted: boolean }): void {
  if (event.persisted && owner !== null) setOwner(null);
}

/**
 * Runs `run` unless a checkout is already starting. Returns false, without
 * calling `run`, when the lock is taken. Releases the lock unless `run`
 * resolves "redirecting"; errors from `run` are rethrown after release.
 *
 * `isCurrent()` turns false once this run no longer owns the lock (safety
 * timeout, bfcache restore). `run` must check it after every await and stop
 * before redirecting, navigating or sending events, or a late Basic session
 * could still open after the buyer chose Premium.
 */
export async function runExclusiveCheckout(
  runOwner: string,
  run: (isCurrent: () => boolean) => Promise<CheckoutRunResult>,
): Promise<boolean> {
  if (owner !== null) return false;
  const thisRun = ++lastRun;
  setOwner(runOwner, thisRun);
  const isCurrent = () => owner !== null && ownerRun === thisRun;
  let result: CheckoutRunResult = "released";
  try {
    result = await run(isCurrent);
  } finally {
    if (result !== "redirecting") releaseRun(thisRun);
  }
  return true;
}
